import * as React from 'react'
import { Outlet, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { requireAnyPermission } from '@/lib/permissions/route-guards'
import { LEARNING_PREFIX } from '@/lib/permissions/learning-permissions'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import { useLdSettled } from '@/hooks/use-ld-settled'

/**
 * Layout route for the whole Learning area (sc-1245).
 *
 * Two gates, in this order:
 *
 *   1. `learningPortal` (LaunchDarkly) decides whether the area exists at all.
 *      Off means the nav item is hidden AND this route redirects, so there is
 *      no URL that reaches Learning. This gate DOMINATES the permission below:
 *      `learning.catalog` is becoming broadly held (granted to new users,
 *      backfilled for existing ones), so it no longer keeps anyone out.
 *   2. Holding ANY `learning.*` permission, mirroring the Backoffice layout
 *      route. The landing page has no key of its own — an author or a reporter
 *      who is not enrolled in anything still belongs in the area — so the
 *      prefix is the right check and each leaf adds its own on top.
 *
 * The flag cannot be checked in `beforeLoad` alongside the permission: LD only
 * resolves in the browser, so there is nothing to read when the guard runs.
 * Hence a component gate here rather than a second `beforeLoad` clause.
 */
export const Route = createFileRoute('/_app/learning')({
  beforeLoad: requireAnyPermission(LEARNING_PREFIX),
  component: LearningLayout,
})

function LearningLayout() {
  const { learningPortal } = useFeatureFlags()
  const ldSettled = useLdSettled()
  const navigate = useNavigate()
  const flagOff = ldSettled && !learningPortal

  React.useEffect(() => {
    if (flagOff) void navigate({ to: '/home', replace: true })
  }, [flagOff, navigate])

  // Hold the spinner through both the unsettled window and the redirect, so the
  // area never flashes content it is about to navigate away from. Waiting on
  // `ldSettled` is what keeps a flagged-in user from being bounced on a cold
  // load or a deep link, before LD has spoken.
  if (!ldSettled || flagOff) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return <Outlet />
}
